import React, { useContext, useEffect, useState } from 'react'
import { motion } from "framer-motion";
import { dashboardMockup } from '../assets/images';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { apiGet, apiPost } from '../utils/api'
import { useNotify } from '../components/NotificationComponent';
import CountdownTimer from '../components/CountdownTimer';
import { LOGO_IMAGES } from '../constants/LogoImages';
import { BORDER_COLORS_FOR_LOGOS } from '../constants/LogoBorderColors';
import { AdAccounts } from '../constants/AdAccounts';
import { LoadingComponent } from '../components';

const HomePage = () => {
  const navigate = useNavigate()
  const {notify} = useNotify()
  const { userInfo } = useContext(UserContext)
  const [userMetaInfo, setUserMetaInfo] = useState({})
  const [loadingTrailStatus, setLoadingTrailStatus] = useState(true)

  const handleTrialClick = () => {
    if(userInfo.access){
        apiPost('start-trial/', {}).then(res => {
          notify('trial has been started for 7 days.');
        }).catch(err => {
          notify(err.response && err.response?.data?.detail ? err.response?.data.detail : "Failed to start trial");
        })
    }
    else{
      navigate('/login')
    }
  }

  useEffect(()=>{
    if(userInfo?.access){
      setLoadingTrailStatus(true)
      apiGet('user-info/').then(res => {
        setUserMetaInfo(res)
      }).catch(err => {
        notify(err.response && err.response.data.detail ? err.response.data.detail : 'failed to get user info')
      }).finally(()=>{
          setLoadingTrailStatus(false)
      })
    }
    else{
      setLoadingTrailStatus(false)
    }
    // eslint-disable-next-line
  },[userInfo?.access])

  return (
    <div>
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center py-20 px-4"
      >
        <h1 className="text-5xl font-extrabold text-gray-800 leading-tight mb-4">
          Turn Data into High-Converting Ads
        </h1>
        <p className="text-gray-600 text-xl mb-6">
          Your unified AI platform to optimize ads, increase ROI, and understand your audience like never before.
        </p>
        {!loadingTrailStatus ?
          <>
            {userMetaInfo?.trial?.is_trial ?
              <CountdownTimer endTime={userMetaInfo?.trial?.trial_ends_at} />
            :
              <button onClick={()=>handleTrialClick()} className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:opacity-90 transition">
                Try Free for 7 Days
              </button>
            }
          </>: 
          (
            <div className=' w-full flex justify-center items-center'>
              <LoadingComponent className='border-purple-500 h-10 w-10 mt-6 '/>
            </div>
          )}
      </motion.section>

      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="py-16 px-6 max-w-6xl mx-auto"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
          <div className="bg-white shadow hover:shadow-lg transition p-6 rounded-xl">
            <h3 className="text-xl font-semibold mb-2">🔗 Connect Your Platforms</h3>
            <p className="text-gray-600">Securely link Google, Meta, LinkedIn, and more with a few clicks and track everything in one view.</p>
          </div>
          <div className="bg-white shadow hover:shadow-lg transition p-6 rounded-xl">
            <h3 className="text-xl font-semibold mb-2">🧠 AI Insights</h3>
            <p className="text-gray-600">Get smart suggestions backed by behavioral psychology and live ad trends.</p>
          </div>
          <div className="bg-white shadow hover:shadow-lg transition p-6 rounded-xl">
            <h3 className="text-xl font-semibold mb-2">📈 Real-time Optimization</h3>
            <p className="text-gray-600">Boost your conversions with real-time alerts and AI-powered tweaks.</p>
          </div>
        </div>
      </motion.section>

      {/* Integration Logos */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50 px-4">
        <h2 className="text-center text-3xl font-bold text-gray-800 mb-8">Seamless Integrations</h2>
        <div className="flex flex-wrap justify-center items-center gap-8">
          {AdAccounts.map((name, i) => (
            // <div key={i} className="text-gray-600 cursor-pointer text-lg font-medium bg-white px-6 py-3 rounded shadow hover:shadow-md transition">{name}</div>
            <div key={i} style={{ borderColor: BORDER_COLORS_FOR_LOGOS[name] }} className={`flex items-center gap-2 border-l-4 pl-4 bg-white py-3 px-6 rounded shadow hover:shadow-md transition`}>
              <img src={LOGO_IMAGES[name]} alt='' className="w-5 h-5" />
              <span className="text-gray-700 font-medium">{name}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">A Sneak Peek Into the Dashboard</h2>
        <div className="min-h-64 bg-gray-200 rounded-xl shadow-inner flex items-center justify-center text-gray-500">
          {!dashboardMockup ?
            <p>Loading...</p>
            :
            <img src={dashboardMockup} alt='' className='w-[100%]'  />
          }
        </div>
      </section>
      

      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="bg-gradient-to-r from-blue-50 to-indigo-50 py-16 px-6"
      >
        <h2 className="text-3xl font-bold text-center mb-12">Trusted by Growing Teams</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {text:'The insights are incredibly precise. We saw a 30% increase in ROI in just 2 weeks.', user:'Riya Patel, Ecom Founder'},
            {text:'I love how it explains the psychology behind every ad suggestion. Genius.', user:'Arjun Mehra, Performance Marketer'},
            {text:'This tool replaced my ad manager and analyst. All in one!', user:"Komal Singh, Growth Consultant"}].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.02 }}
              className="bg-white shadow-md p-6 rounded-xl text-center"
            >
              <p className="text-gray-600 italic mb-4">“{item.text}”</p>
              <p className="text-gray-800 font-semibold">— {item.user}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-white">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Why Choose AdMentorAI?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h3 className="text-xl font-semibold mb-2">✅ More than Analytics</h3>
            <p className="text-gray-600">We don’t just show charts. We tell you what’s working, why, and what to change.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">✅ Behavior + AI Combined</h3>
            <p className="text-gray-600">Powered by human psychology + advanced machine learning models.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">✅ Unified, Real-Time Data</h3>
            <p className="text-gray-600">One dashboard. All platforms. Zero confusion.</p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">✅ Built for Agencies & Solo Founders</h3>
            <p className="text-gray-600">Scales with you — whether it’s 1 ad account or 100.</p>
          </div>
        </div>
      </section>

       {/* FAQ */}
       <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Frequently Asked Questions</h2>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg text-gray-800">Is it secure to connect my ad accounts?</h3>
            <p className="text-gray-600">Absolutely. We use industry-standard OAuth authentication and never store sensitive credentials.</p>
          </div>
          <div>
            <h3 className="font-semibold text-lg text-gray-800">What if I don’t have technical knowledge?</h3>
            <p className="text-gray-600">AdMentorAI is built for everyone. You don’t need to be technical — just connect, view, and act.</p>
          </div>
          <div>
            <h3 className="font-semibold text-lg text-gray-800">How accurate are the predictions?</h3>
            <p className="text-gray-600">Our models are trained on billions of impressions. While no AI is perfect, you’ll consistently get 10–30% performance boosts.</p>
          </div>
        </div>
      </section>

      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="text-center py-20 px-6 bg-gradient-to-br from-blue-50 to-indigo-50"
      >
        <h2 className="text-4xl font-extrabold mb-4 text-gray-800">Ready to Outperform Your Competitors ?</h2>
        <p className="text-gray-600 text-lg mb-8">Join 500+ brands using AdMentorAI to scale intelligently.</p>
        {userMetaInfo?.trial ?
          <CountdownTimer endTime={userMetaInfo?.trial?.trial_ends_at} />
        :
          <button onClick={()=>handleTrialClick()} className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:opacity-90 transition">
            Try Free for 7 Days
          </button>
        }
      </motion.section>
    </div>
  )
}

export default HomePage